import {
  CompleteMultipartDirectUploadPart,
  MultipartDirectUploadPart,
} from '@sparemin/api-sdk';

/**
 * Number of parts uploaded concurrently.
 */
const MULTIPART_UPLOAD_CONCURRENCY = 3;

/**
 * Per-part upload timeout. `fetch` has no timeout of its own, so a dead
 * connection that neither errors nor completes would stall a part PUT — and
 * therefore the whole upload — indefinitely. Generous enough that a legitimate
 * (even slow) part transfer never trips it; only a genuinely stuck request does.
 */
const PART_UPLOAD_TIMEOUT_MILLIS = 5 * 60 * 1000;

/**
 * Result of uploading a single part.
 * Extends the SDK type with `chunkSize` for accurate progress tracking.
 */
export type UploadedPart = CompleteMultipartDirectUploadPart & {
  /**
   * Byte length of the uploaded chunk.
   */
  chunkSize: number;
};

/**
 * Calculates the 1-based part numbers needed to upload a file.
 *
 * @param fileSize - Total file size in bytes.
 * @param partSizeBytes - Part size in bytes as returned by the init endpoint.
 * @param maxParts - Maximum number of parts allowed (from the init response).
 *
 * @returns Array of 1-based part numbers, e.g. [1, 2, 3].
 */
export function getMultipartPartNumbers(
  fileSize: number,
  partSizeBytes: number,
  maxParts: number,
): number[] {
  const numParts = Math.min(Math.ceil(fileSize / partSizeBytes), maxParts);

  return Array.from({ length: numParts }, (_, i) => i + 1);
}

/**
 * Uploads a single file chunk to its S3 presigned URL.
 *
 * Retries the PUT once on failure before throwing. The ETag from the response
 * header is returned as-is (with surrounding quotes) for use in the complete
 * call. Each attempt is aborted after `PART_UPLOAD_TIMEOUT_MILLIS` so a hung
 * connection can't stall the upload forever.
 *
 * @param file - The full file being uploaded.
 * @param partNumber - 1-based part number.
 * @param presignedUrl - S3 presigned PUT URL for this part.
 * @param partSizeBytes - Part size in bytes; used to slice the correct chunk.
 */
export async function uploadPart(
  file: File,
  partNumber: number,
  presignedUrl: string,
  partSizeBytes: number,
): Promise<UploadedPart> {
  const start = (partNumber - 1) * partSizeBytes;
  const end = Math.min(start + partSizeBytes, file.size);
  const blob = file.slice(start, end);

  const attempt = async () => {
    const controller = new AbortController();
    let timedOut = false;

    const timeoutId = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, PART_UPLOAD_TIMEOUT_MILLIS);

    try {
      const res = await fetch(presignedUrl, {
        method: 'PUT',
        body: blob,
        signal: controller.signal,
      });

      if (!res.ok) {
        throw new Error(
          `Part ${partNumber} upload failed with status ${res.status}`,
        );
      }

      const etag = res.headers.get('ETag');

      if (!etag) {
        throw new Error(`Part ${partNumber} response missing ETag header`);
      }

      return etag;
    } catch (error) {
      // A timeout surfaces as an AbortError — rethrow it as a clear timeout.
      if (timedOut) {
        throw new Error(
          `Part ${partNumber} upload timed out after ${PART_UPLOAD_TIMEOUT_MILLIS}ms`,
        );
      }

      throw error;
    } finally {
      clearTimeout(timeoutId);
    }
  };

  let etag: string;

  try {
    etag = await attempt();
  } catch {
    // Retry once if the first attempt fails.
    etag = await attempt();
  }

  return { partNumber, etag, chunkSize: blob.size };
}

/**
 * Runs `worker(item)` over `items` with at most `limit` operations in flight.
 *
 * Uses a shared cursor and `limit` runners so the next item is picked up the
 * moment any runner finishes — no batch-level synchronization barrier.
 */
async function runWithConcurrency<T, R>(
  items: T[],
  limit: number,
  worker: (item: T, index: number) => Promise<R>,
): Promise<R[]> {
  const results: R[] = new Array(items.length);
  let nextIndex = 0;

  const runner = async () => {
    let i = nextIndex;

    nextIndex += 1;

    while (i < items.length) {
      results[i] = await worker(items[i], i);
      i = nextIndex;
      nextIndex += 1;
    }
  };

  const runnerCount = Math.min(limit, items.length);
  const runners = Array.from({ length: runnerCount }, () => runner());

  await Promise.all(runners);

  return results;
}

/**
 * Uploads all presigned parts with bounded concurrency and progress reporting.
 *
 * Up to `MULTIPART_UPLOAD_CONCURRENCY` parts upload at a time; as soon as any
 * part finishes, the next queued part starts. Progress is reported after each
 * part completes, calculated as bytes uploaded / total file size so the last
 * (smaller) part doesn't distort the percentage.
 *
 * The returned array is sorted by `partNumber` ascending, as required by S3's
 * complete-multipart-upload call.
 *
 * @param file - The full file being uploaded.
 * @param presignedParts - Presigned part entries from the presign-parts endpoint.
 * @param partSizeBytes - Part size in bytes as returned by the init endpoint.
 * @param onProgress - Called after each part with a 0–100 progress value.
 *
 * @returns Parts sorted by partNumber, ready to pass to the complete endpoint.
 */
export async function uploadPartsWithProgress(
  file: File,
  presignedParts: MultipartDirectUploadPart[],
  partSizeBytes: number,
  onProgress: (progress: number) => void,
): Promise<CompleteMultipartDirectUploadPart[]> {
  let bytesUploaded = 0;

  const uploadAndTrack = async ({
    partNumber,
    url,
  }: MultipartDirectUploadPart): Promise<CompleteMultipartDirectUploadPart> => {
    const result = await uploadPart(file, partNumber, url, partSizeBytes);

    bytesUploaded += result.chunkSize;
    onProgress(Math.round((bytesUploaded / file.size) * 100));

    return { partNumber: result.partNumber, etag: result.etag };
  };

  return runWithConcurrency(
    presignedParts,
    MULTIPART_UPLOAD_CONCURRENCY,
    uploadAndTrack,
  );
}
